const normaliseRouteRoot = () => {
  const routeRoot = window.Shopify?.routes?.root || '/';
  return routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
};

// EU migration: shared US/EU theme code must map source-store product tile data to the current store before ATC.
const normaliseValue = (value) => String(value || '').trim().toLowerCase();

const getProductHandle = ({ productHandle, product } = {}) => {
  if (productHandle) return productHandle;
  if (product?.handle) return product.handle;

  const url = product?.url || product?.product_url;
  const match = String(url || '').match(/\/products\/([^/?#]+)/);
  return match?.[1] || null;
};

const variantMatches = (storeVariant, selectedVariant) => {
  if (!storeVariant || !selectedVariant) return false;

  if (Number(storeVariant.id) === Number(selectedVariant.id)) {
    return true;
  }

  const storeSku = normaliseValue(storeVariant.sku);
  const selectedSku = normaliseValue(selectedVariant.sku);
  if (storeSku && selectedSku && storeSku === selectedSku) {
    return true;
  }

  const storeTitle = normaliseValue(storeVariant.title);
  const selectedTitle = normaliseValue(selectedVariant.title || selectedVariant.name);
  if (storeTitle && selectedTitle && storeTitle === selectedTitle) {
    return true;
  }

  const selectedOptions = selectedVariant.options || [
    selectedVariant.option1,
    selectedVariant.option2,
    selectedVariant.option3
  ].filter(Boolean);

  if (!selectedOptions.length) return false;

  return selectedOptions.every((option, index) => (
    normaliseValue(storeVariant[`option${index + 1}`]) === normaliseValue(option)
  ));
};

export const resolveCurrentStoreVariantId = async ({
  variantId,
  productHandle,
  product = null,
  selectedVariant = null
} = {}) => {
  const handle = getProductHandle({ productHandle, product });
  if (!handle) return variantId;

  try {
    // Resolve from /products/{handle}.js on the current domain so US and EU each use their own variant IDs.
    const response = await fetch(`${normaliseRouteRoot()}products/${handle}.js`);
    if (!response.ok) return variantId;

    const product = await response.json();
    const variants = product?.variants || [];
    if (!variants.length) return variantId;

    const sameIdVariant = variants.find((variant) => Number(variant.id) === Number(variantId));
    if (sameIdVariant) return sameIdVariant.id;

    const matchedVariant = variants.find((variant) => variantMatches(variant, selectedVariant));
    if (matchedVariant) return matchedVariant.id;

    if (variants.length === 1) return variants[0].id;
  } catch (error) {
    if (window.enableDebugMode) {
      console.warn('Unable to resolve current store variant ID:', { error, variantId, productHandle: handle });
    }
  }

  return variantId;
};
